import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { Field } from '../../types';
const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  textarea: 'Long text',
  select: 'Picklist',
  currency: 'Currency',
  percent: 'Percent',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  user: 'Related record',
  userLookup: 'User lookup'
};
interface AddFieldPickerProps {
  sectionTitle: string;
  /** Fields not currently placed in any section */
  available: Field[];
  onCancel: () => void;
  onAdd: (fieldId: string) => void;
}

/** Searchable list of unplaced loan fields to drop into a section. */
export function AddFieldPicker({
  sectionTitle,
  available,
  onCancel,
  onAdd
}: AddFieldPickerProps) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...available].sort((a, b) => a.label.localeCompare(b.label));
    if (!q) return sorted;
    return sorted.filter(f => f.label.toLowerCase().includes(q) || (TYPE_LABELS[f.type] ?? f.type).toLowerCase().includes(q));
  }, [available, query]);
  useEffect(() => {
    setHighlight(0);
  }, [query]);
  useEffect(() => {
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [highlight]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const field = results[highlight];
      if (field) onAdd(field.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };
  return <div className="rounded-lg border border-blue-300 bg-white shadow-lg ring-2 ring-blue-100" data-id="element-1" data-mp="19czqfoux7yk8">
      <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5" data-id="element-2" data-mp="1e7vkeujpwvzz">
        <SearchIcon className="h-4 w-4 shrink-0 text-slate-400" data-id="element-3" data-mp="1ias8h0s11kbb" />
        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder={`Search fields to add to ${sectionTitle}…`} aria-label={`Search fields to add to ${sectionTitle}`} aria-autocomplete="list" className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400" data-id="element-4" data-mp="lmdjy20kemcc" />
        <button type="button" onClick={onCancel} aria-label="Cancel adding a field" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100" data-id="element-5" data-mp="2sqfrl010ndq">
          <XIcon className="h-4 w-4" data-id="element-6" data-mp="3qk9ld6g7l8iv" />
        </button>
      </div>

      <ul ref={listRef} role="listbox" aria-label="Available fields" className="max-h-56 overflow-y-auto py-1" data-id="element-7" data-mp="3mhcy32fq4o8l">
        {available.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400" data-id="element-8" data-mp="3cjf9gdtycpfg">
            Every available field is already placed on the layout.
          </li>}

        {available.length > 0 && results.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400" data-id="element-9" data-mp="32suer1heg51t">
            No available fields match “{query}”.
          </li>}

        {results.map((field, index) => <li key={field.id} data-id="element-10" data-mp="2tth2k11w52de" data-mpi={index}>
            <button type="button" role="option" aria-selected={index === highlight} onMouseEnter={() => setHighlight(index)} onClick={() => onAdd(field.id)} className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${index === highlight ? 'bg-blue-50' : ''}`} data-id="element-11" data-mp="2trmhmmqmda9b">
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-800" data-id="element-12" data-mp="j0mxhqpi4pm7">
                {field.label}
              </span>
              <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-13" data-mp="a0gpgkckohuf">
                {TYPE_LABELS[field.type] ?? field.type}
              </span>
            </button>
          </li>)}
      </ul>
    </div>;
}